'use client';

import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@routly/ui';
import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useCreateUser } from '@/hooks/useUserActions';
import { USER_ROLES, type UserRole } from '@/lib/types';

/**
 * Creating an account.
 *
 * The password is set here and handed over out of band, because the product has
 * no mail sender: an invite link nobody can deliver would be worse than a
 * temporary password an administrator passes on in person. It is validated
 * against the same eight characters the sign-in form asks for, so an account
 * cannot be created that its owner is then unable to use.
 */
const MIN_PASSWORD = 8;

interface InviteUserDialogProps {
  /**
   * Fixes the account's organisation and hides the picker — for the customer's
   * own page, where asking which organisation this is would be a trick
   * question.
   */
  organizationId?: string;
}

export function InviteUserDialog({ organizationId: fixedOrg }: InviteUserDialogProps = {}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('USER');
  const [pickedOrg, setPickedOrg] = useState('');

  const organizationId = fixedOrg ?? pickedOrg;
  // Only the picker needs the list, and on a customer's page there is no picker.
  const { data: organizations } = useOrganizations({ limit: 100, enabled: !fixedOrg });
  const createUser = useCreateUser();

  const isValid =
    name.trim() !== '' &&
    /\S+@\S+\.\S+/.test(email) &&
    password.length >= MIN_PASSWORD &&
    organizationId !== '';

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isValid || createUser.isPending) return;

    try {
      await createUser.mutateAsync({
        name: name.trim(),
        email: email.trim(),
        password,
        role,
        organizationId,
      });
      toast.success(`Invited ${email.trim()}`);
      setOpen(false);
      setName('');
      setEmail('');
      setPassword('');
      setRole('USER');
    } catch (caught) {
      toast.error('Could not create the account', {
        description: caught instanceof Error ? caught.message : undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="mr-2 h-4 w-4" />
          Invite
        </Button>
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>New account</DialogTitle>
            <DialogDescription>
              The password is set here and handed over in person — there is no mail sender.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="invite-name">Name</Label>
            <Input id="invite-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="invite-password">Temporary password</Label>
            <Input
              id="invite-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby="invite-password-hint"
            />
            <p id="invite-password-hint" className="text-xs text-muted-foreground">
              At least {MIN_PASSWORD} characters.
            </p>
          </div>

          {!fixedOrg && (
            <div className="space-y-2">
              <Label htmlFor="invite-org">Organization</Label>
              <Select value={pickedOrg} onValueChange={setPickedOrg}>
                <SelectTrigger id="invite-org">
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent>
                  {(organizations?.data ?? []).map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="invite-role">Role</Label>
            <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
              <SelectTrigger id="invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {USER_ROLES.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={!isValid || createUser.isPending}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
